import { useState } from 'react';

// mui
import {
    Grid,
    ListItemText,
    Stack,
    Typography,
    ButtonBase,
    Box,
    Skeleton,
    IconButton,
    Menu,
    MenuItem,
    Tooltip,
} from '@mui/material';
import { MoreHoriz as MoreHorizIcon } from '@mui/icons-material';

// component
import { addNavigation, createProfileProp, requireLogin, copyLink } from '../';

// api
import { Report } from '../../api';

// client-shared
import { UserAvatar, UserBadges, UserLocation, ReportDialog } from '@goldblack/client-comps';
import { storage } from '@goldblack/client-storage';
import { def } from '@goldblack/shared';

function PostShowMediaHeader(props) {
    const [menuAnchor, setMenuAnchor] = useState(null);
    const [openReport, setOpenReport] = useState(false);
    const [reason, setReason] = useState('');
    const [reportOkDisabled, setReportOkDisabled] = useState(true);
    const [reportStatus, setReportStatus] = useState(null);

    const self = storage.localStorage.getValue('user');
    const isOwnPost = self?._id && props.user?._id && String(self._id) === String(props.user._id);

    const onClickAvatar = () => {
        if (!props.user) return; // author still loading
        props.onClose?.();
        addNavigation(createProfileProp(props.user));
    };

    const getSourceDesc = (source) => {
        switch (source) {
            case 'recommend':
                return 'Recommended for you';
            case 'following':
                return '';
            default:
                return '';
        }
    };

    const onClickMore = (e) => {
        setMenuAnchor(e.currentTarget);
    };

    const onCloseMenu = () => {
        setMenuAnchor(null);
    };

    const onClickReport = () => {
        setMenuAnchor(null);
        if (!requireLogin()) return;
        setOpenReport(true);
    };

    const onReportOK = () => {
        setOpenReport(false);
        Report('post', props.post._id, reason, () => {
            setReportStatus(def.ReportStatus.PENDING);
            props.onReport?.();
        });
    };

    const onReportCancel = () => {
        setOpenReport(false);
    };

    const onReportClose = () => {
        setOpenReport(false);
    };

    const onReportChange = (event, value) => {
        if (value) {
            setReason(value.code);
            setReportOkDisabled(false);
        } else {
            setReason('');
            setReportOkDisabled(true);
        }
    };

    return (
        <Grid
            container
            spacing={0}
            direction='row'
            justifyContent='flex-start'
            alignItems='center'
            sx={{
                width: '100%',
                position: 'absolute',
                top: '0%',
                left: '0%',
                transform: 'translate(0%, 0%)',
                background: 'linear-gradient( #0000007f, #00000000)',
                m: 0,
                p: 1,
                userSelect: 'none',
            }}>
            <ButtonBase disableRipple onClick={onClickAvatar} aria-label='open profile'>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, overflow: 'visible' }}>
                    <UserAvatar loading={props.loading} variant='post' user={props.user} config={props.config} />
                    <ListItemText
                        sx={{ m: 0, p: 0 }}
                        primary={
                            props.loading ? (
                                <Skeleton variant='rectangular' />
                            ) : (
                                <Stack direction='row' alignItems='center' gap={0}>
                                    <Typography variant='body2' noWrap color='text.primary'>
                                        {props.user?.username}
                                    </Typography>
                                    <UserBadges badges={props.user?.badges} />
                                    <UserLocation {...props.user?.location} />
                                </Stack>
                            )
                        }
                        secondary={
                            props.loading ? (
                                <Skeleton variant='rectangular' />
                            ) : (
                                <Typography color='secondary.light' variant='caption' sx={{ opacity: 0.8 }}>
                                    {getSourceDesc(props.user?.source)}
                                </Typography>
                            )
                        }
                    />
                </Box>
            </ButtonBase>
            {!isOwnPost &&
                !props.loading &&
                props.post?.status !== def.PostStatus.BLOCKED &&
                props.post?.status !== def.PostStatus.CENSORED &&
                props.post?.status !== def.PostStatus.PROCESSING && (
                    <Box sx={{ ml: 'auto' }}>
                        <IconButton
                            size='small'
                            aria-label='post options'
                            onClick={onClickMore}
                            sx={{ color: 'secondary.main' }}>
                            <Tooltip title='options'>
                                <MoreHorizIcon fontSize='small' />
                            </Tooltip>
                        </IconButton>
                        <Menu
                            anchorEl={menuAnchor}
                            open={Boolean(menuAnchor)}
                            onClose={onCloseMenu}
                            slotProps={{ paper: { elevation: 0, variant: 'outlined' } }}>
                            <MenuItem
                                onClick={() => {
                                    setMenuAnchor(null);
                                    copyLink(`/@${encodeURIComponent(props.user.username)}/${props.post._id}`);
                                }}
                                sx={{ fontSize: '14px' }}>
                                Copy link
                            </MenuItem>
                            <MenuItem
                                onClick={onClickReport}
                                disabled={reportStatus === def.ReportStatus.PENDING}
                                sx={{ fontSize: '14px' }}>
                                Report
                            </MenuItem>
                        </Menu>
                    </Box>
                )}
            <ReportDialog
                type='post'
                open={openReport}
                title='Report Post'
                desc='What would you like to report to us?'
                onClose={onReportClose}
                onOK={onReportOK}
                onCancel={onReportCancel}
                okDisabled={reportOkDisabled}
                onChange={onReportChange}
            />
        </Grid>
    );
}

export { PostShowMediaHeader };
